import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import ScrollReveal from '../components/ScrollReveal';

export default function Services() {
  const [ydelser, setYdelser] = useState([]);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/ydelser')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setYdelser(data);
        }
      })
      .catch(err => console.error('Fejl ved hentning af ydelser:', err));
  }, []);

  return (
    <>
      <SEO 
        title="Tømrerydelser på Fyn" 
        description="Oversigt over professionelt tømrerarbejde udført af Fyns - Tømreren ApS: Tag & Facade, Interiør & Gulve, Køkken & Inventar samt autoriseret asbestsanering på hele Fyn." 
      />

      {/* Page Header */}
      <section className="bg-[#1F2A37] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Vores Ydelser & Specialer
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-base leading-relaxed">
            Hos <Link to="/" className="text-white hover:text-gray-200 underline decoration-1 underline-offset-2 font-semibold" title="Gå til forsiden for Fyns Tømreren">Fyns - Tømreren ApS</Link> rådgiver, projekterer og udfører vi alle typer tømrer- og snedkeropgaver med høj faglig stolthed og præcision på hele Fyn.
          </p>
        </div>
      </section>

      {/* Services Grid (3 large clickable boxes) */}
      <section className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {ydelser.map((ydelse, idx) => {
              const imgUrl = Array.isArray(ydelse.hero_billede) ? ydelse.hero_billede[0] : ydelse.hero_billede;
              const resizedImg = imgUrl ? imgUrl.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/') : '';

              return (
                <ScrollReveal
                  key={ydelse.id || ydelse.ydelse_slug}
                  animation="fade-up"
                  delay={idx * 150}
                  className="h-full"
                >
                  <Link
                    to={`/ydelser/${ydelse.ydelse_slug}`}
                    className="group bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-200 flex flex-col h-full"
                  >
                    <figure className="relative h-64 overflow-hidden bg-gray-100">
                      <img
                        src={resizedImg}
                        alt={`${ydelse.titel} – Professionelt tømrer- og snedkerarbejde på Fyn udført af Fyns Tømreren`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>
                      <figcaption className="absolute bottom-3 left-3 bg-[#1F2A37]/85 backdrop-blur-xs text-white text-xs px-2.5 py-1 flex items-center gap-1.5 shadow-sm">
                        <i className="fa-solid fa-screwdriver-wrench text-[#3A5A40]"></i>
                        <span>{ydelse.titel}</span>
                      </figcaption>
                    </figure>

                    <div className="p-8 flex-1 flex flex-col justify-between space-y-4">
                      <div className="space-y-3">
                        <h2 className="text-2xl font-bold text-[#111111] group-hover:text-[#3A5A40] transition-colors">
                          {ydelse.titel}
                        </h2>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {ydelse.kort_beskrivelse}
                        </p>
                      </div>

                      <div className="pt-4 border-t border-gray-100 inline-flex items-center gap-2 text-sm font-semibold text-[#3A5A40] group-hover:translate-x-1 transition-transform">
                        <span>Læs mere om {ydelse.titel.toLowerCase()}</span>
                        <i className="fa-solid fa-arrow-right text-xs"></i>
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              );
            })}
          </div>

        </div>
      </section>

      {/* Special Services: Asbest & Træterrasser */}
      <section className="py-16 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Asbest Service */}
            <div className="bg-[#F9FAFB] border border-gray-200 p-8 flex flex-col justify-between shadow-xs">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#d75d5a]/10 text-[#d75d5a] flex items-center justify-center text-xl font-bold">
                  <i className="fa-solid fa-shield-halved"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#111111]">
                  Autoriseret Asbestarbejde
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Vi er autoriserede til professionel og sikker håndtering, nedtagning og lovpligtig sanering af asbestholdige materialer og bølgeeternittage.
                </p>
                <ul className="space-y-2 text-sm text-gray-700 pt-2">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#3A5A40]"></i>
                    <span>Certificeret efter gældende lovgivning</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#3A5A40]"></i>
                    <span>Sikker nedtagning og deponering</span>
                  </li>
                </ul>
              </div>
              <div className="pt-6 flex items-center justify-between gap-4">
                <Link
                  to="/ydelser/asbestsanering"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#d75d5a] hover:text-[#b54a47]"
                >
                  Læs mere om asbestsanering
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </Link>
                <Link
                  to="/kontakt"
                  className="text-xs font-semibold text-gray-500 hover:text-black underline"
                >
                  Indhent tilbud
                </Link>
              </div>
            </div>

            {/* Træterrasser & Uderum */}
            <div className="bg-[#F9FAFB] border border-gray-200 p-8 flex flex-col justify-between shadow-xs">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#3A5A40]/10 text-[#3A5A40] flex items-center justify-center text-xl font-bold">
                  <i className="fa-solid fa-tree"></i>
                </div>
                <h3 className="text-2xl font-bold text-[#111111]">
                  Træterrasser & Udeliv
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Drømmer du om en ny, eksklusiv træterrasse, hegn eller overdækning i haven? Vi opbygger holdbare uderum tilpasset dit hus og havemiljø.
                </p>
                <ul className="space-y-2 text-sm text-gray-700 pt-2">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#3A5A40]"></i>
                    <span>Kvalitetstræ & robuste underkonstruktioner</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#3A5A40]"></i>
                    <span>Specialtilpassede hegn og afskærmninger</span>
                  </li>
                </ul>
              </div>
              <div className="pt-6">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#3A5A40] hover:text-[#2d4632]"
                >
                  Få tilbud på træterrasse
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1F2A37] text-white text-center">
        <div className="max-w-4xl mx-auto px-4 space-y-6">
          <h2 className="text-2xl sm:text-3xl font-bold">Brug for rådgivning eller et tilbud?</h2>
          <p className="text-gray-300 text-sm sm:text-base">
            Vi gennemgår gerne dit projekt sammen med dig og giver et uforpligtende prisoverslag.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-2">
            <Link
              to="/kontakt"
              className="bg-[#d75d5a] hover:bg-[#c44d4a] text-white px-7 py-3 font-semibold transition-all hover:-translate-y-1 shadow-sm hover:shadow-md"
            >
              Indhent tilbud
            </Link>
            <a
              href="tel:60715018"
              className="border border-white bg-transparent hover:bg-transparent text-white px-7 py-3 font-semibold transition-all hover:-translate-y-1 whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-white mr-2"></i>
              60 71 50 18
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
